import React, { useEffect, useState, useRef } from 'react';
import { render } from 'react-dom';
import Wrapper from '../wrapper/wrapper';
import Button from '../button/button';
import { IS_ACTIVE_SERVICE, IS_PARTNER, Roles } from '../../../resources/uis/epc/assets/js/shared/data/consts';
import { List, SignIn, SignOut, X } from '@phosphor-icons/react';
import LabelIcon from './label-icon';
import clsx from 'clsx';
import { clearFilters } from '../../utilities/hooks/use-certificate-overview-filter-persistence';

interface HeaderProps {
  role?: Roles;
  infoBanner?: string;
}

interface NavigationItem {
  name: string;
  href: string;
  sideNavigation: boolean;
}

type NavigationItems = Record<Roles, NavigationItem[]>;

const init = () => {
  const headerContainer = document.getElementById('header');
  if (headerContainer) {
    render(<Header />, headerContainer);
  }
};

const Header: React.FC<HeaderProps> = ({ role, infoBanner }) => {
  const [isOverlayMenuActive, setIsOverlayMenuActive] = useState(false);
  const userRole = role || (window.userRole as Roles);
  const logoutFormRef = useRef<HTMLFormElement>(null);

  const navigationItems: NavigationItems = {
    [Roles.GUEST]: [
      { name: 'Produkte', href: window.routes['epc.ui.tenant.productselection'], sideNavigation: false },
      { name: 'FAQ', href: window.routes.faq, sideNavigation: false },
      { name: 'Immobilienmakler', href: window.routes.realtor, sideNavigation: false },
    ],
    [Roles.USER]: [
      { name: 'Produkte', href: window.routes['epc.selection'], sideNavigation: false },
      { name: 'Meine Energieausweise', href: '/memberarea/orders', sideNavigation: false },
      { name: 'Rechnungen', href: '/memberarea/invoices', sideNavigation: false },
      { name: 'Profil', href: window.routes.profile, sideNavigation: false },
    ],
    [Roles.SERVICE]: [
      { name: 'Meine Energieausweise', href: '/memberarea/orders', sideNavigation: false },
      { name: 'Profil', href: window.routes.profile, sideNavigation: false },
      { name: 'FAQ', href: window.routes.faq, sideNavigation: false },
      { name: 'Rechnungen', href: '/memberarea/invoices', sideNavigation: true },
      { name: 'Stornoanfragen', href: window.routes['cancellation.index'], sideNavigation: true },
      { name: 'Kundendaten', href: '/memberarea/users', sideNavigation: true },
      { name: 'Firmendaten', href: '/memberarea/companies', sideNavigation: true },
      { name: 'Gutscheine', href: '/memberarea/vouchers', sideNavigation: true },
      { name: 'Prepaid', href: '/memberarea/products/prepaids', sideNavigation: true },
      { name: 'CSV', href: '/memberarea/orders/csv/create', sideNavigation: true },
      { name: 'Fullservice Bearbeitung', href: '/memberarea/orders/fs', sideNavigation: true },
    ],
    [Roles.CONSULTANT]: [
      { name: 'Meine Energieausweise', href: '/memberarea/orders', sideNavigation: false },
      { name: 'Profil', href: window.routes.profile, sideNavigation: false },
    ],
    [Roles.QS]: [
      { name: 'Meine Energieausweise', href: '/memberarea/orders', sideNavigation: false },
      { name: 'Profil', href: window.routes.profile, sideNavigation: false },
    ],
    [Roles.SURVEYOR]: [{ name: 'Profil', href: window.routes.profile, sideNavigation: false }],
    [Roles.KPI]: [
      { name: 'KPI', href: '/memberarea/kpi', sideNavigation: false },
      { name: 'Kooperationspartner', href: '/memberarea/partners', sideNavigation: false },
      { name: 'Profil', href: window.routes.profile, sideNavigation: false },
    ],
  };

  useEffect(() => {
    const handleResize = () => {
      setIsOverlayMenuActive(false);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleLogout = () => {
    clearFilters();
    logoutFormRef.current?.submit();
  };

  return (
    <>
      {infoBanner && (
        <div className="bg-yellow py-1">
          <Wrapper>{infoBanner}</Wrapper>
        </div>
      )}
      <header className="font-sans bg-white w-full py-4 mb-6 shadow-md">
        <Wrapper>
          <div className="flex justify-between items-center min-h-[44px]">
            {/* Left Part of Navigation: Navigation Items  */}
            <div className="flex gap-16 items-center ">
              {IS_PARTNER() ? (
                <img className="h-8" src="/logos/ea48.svg" alt="Energieausweis48 Logo" />
              ) : (
                <a href={window.routes.homepage}>
                  <img className="h-8" src="/logos/ea48.svg" alt="Energieausweis48 Logo" />
                </a>
              )}
              <ul className="display-none md:flex gap-x-8 gap-y-2 flex-wrap items-center text-md mb-[0]">
                {navigationItems[userRole]?.map(
                  (item: NavigationItem) =>
                    !item.sideNavigation &&
                    !IS_PARTNER() && (
                      <li key={item.name}>
                        <a
                          href={item.href}
                          className="text-black hover:text-primary-200 focus:text-primary-200 hover:no-underline focus:no-underline"
                        >
                          {item.name}
                        </a>
                      </li>
                    )
                )}
              </ul>
            </div>

            {/* Right Part of Navigation: Action Items  */}
            <div className="flex gap-4 items-center shrink-0">
              {(userRole === Roles.GUEST || userRole === Roles.USER) && !IS_ACTIVE_SERVICE() && !IS_PARTNER() && (
                <Button
                  className="display-none md:inline-block"
                  href={
                    userRole === Roles.GUEST
                      ? window.routes['epc.ui.tenant.productselection']
                      : window.routes['epc.selection']
                  }
                  variant="primary"
                  size="small"
                >
                  Energieausweis bestellen
                </Button>
              )}
              {/* Mobile Overlay Menu Trigger */}
              {!IS_PARTNER() && (
                <LabelIcon
                  className="md:hidden"
                  label="Menü"
                  icon={<List />}
                  onClick={() => setIsOverlayMenuActive(true)}
                />
              )}
              {/* Desktop Overlay Menu Trigger.
              Only rendered when one NavigationItem of the Role has sideNavigation true*/}
              {navigationItems[userRole]?.some((item: NavigationItem) => item.sideNavigation) && (
                <LabelIcon
                  className="display-none md:flex"
                  label="Menü"
                  icon={<List />}
                  onClick={() => setIsOverlayMenuActive(true)}
                />
              )}
              {/* Login/Logout Button */}
              {!IS_PARTNER() &&
                (userRole === Roles.GUEST ? (
                  <LabelIcon
                    className="display-none md:flex"
                    href={window.routes.login}
                    label="Anmelden"
                    icon={<SignIn />}
                  />
                ) : (
                  !navigationItems[userRole]?.some((item: NavigationItem) => item.sideNavigation) && (
                    <form
                      ref={logoutFormRef}
                      method="post"
                      action={window.routes.logout}
                      className="display-none md:block"
                    >
                      {/* ADD CSRF HIDDEN INPUT */}
                      <input type="hidden" name="_token" value={window.csrfToken} />
                      <LabelIcon label="Abmelden" icon={<SignOut />} onClick={handleLogout} />
                    </form>
                  )
                ))}
              {/* Tenant Logo */}
              {window?.ea48?.tenantConfiguration?.logo && (
                <img
                  className="h-8"
                  src={`data:${window.ea48.tenantConfiguration.logo.mime};base64,${window.ea48.tenantConfiguration.logo.content}`}
                  alt="Partner Logo"
                />
              )}
            </div>
          </div>
        </Wrapper>
      </header>

      {/* Mobile and Side Navigation Overlay  */}
      {isOverlayMenuActive && (
        <>
          <div className="fixed top-[0] right-[0] left-[0] z-[2002] max-h-[100%] bg-white md:h-full overflow-y-scroll p-4 md:pl-8 md:left-auto md:pr-[calc((100vw-750px)/2)] lg:pr-[calc((100vw-970px)/2)] xl:pr-[calc((100vw-1140px)/2)]">
            <div className="flex flex-col h-full justify-between gap-10 md:min-w-[320px]">
              <div className="flex flex-col md:gap-10">
                <div className="flex justify-between items-center h-11 md:h-auto md:justify-end">
                  <img className="h-8 md:hidden" src="/logos/ea48.svg" alt="Energieausweis48 Logo" />
                  <div
                    className="text-[32px] cursor-pointer self-start md:self-end"
                    onClick={() => setIsOverlayMenuActive(false)}
                  >
                    <X />
                  </div>
                </div>
                <ul className="text-lg md:text-xl flex flex-col">
                  {navigationItems[userRole]?.map((item: NavigationItem, index) => (
                    <li
                      className={clsx(
                        !item.sideNavigation && 'md:hidden',
                        index + 1 !== navigationItems[userRole].length && 'border-b border-grey-200'
                      )}
                      key={item.name}
                    >
                      <a href={item.href} className="py-3 md:py-4 block text-black hover:text-black hover:no-underline">
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col gap-4">
                {userRole === Roles.GUEST && (
                  <Button variant="secondary" href={window.routes.login}>
                    Anmelden
                  </Button>
                )}
                {(userRole === Roles.GUEST || userRole === Roles.USER) && !IS_PARTNER() && (
                  <Button
                    variant="primary"
                    href={
                      userRole === Roles.GUEST
                        ? window.routes['epc.ui.tenant.productselection']
                        : window.routes['epc.selection']
                    }
                  >
                    Energieausweis bestellen
                  </Button>
                )}
                {(userRole === Roles.SERVICE ||
                  userRole === Roles.CONSULTANT ||
                  userRole === Roles.SURVEYOR ||
                  userRole === Roles.USER ||
                  userRole === Roles.QS) && (
                  <form ref={logoutFormRef} method="post" action={window.routes.logout} className="w-full">
                    <input type="hidden" name="_token" value={window.csrfToken} />
                    <Button variant="secondary" fullWidth onClick={handleLogout}>
                      Abmelden
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
          {/* Grey Content Overlay */}
          <div
            className="fixed h-full left-[0] top-[0] bg-black z-[2001] w-full opacity-40"
            onClick={() => setIsOverlayMenuActive(false)}
          />
        </>
      )}
    </>
  );
};

export default Header;
init();
